import uuid from "../../utils/uuid";
import { ContentItem, Field, Metadata } from "./types";

export function generateTemplateFragmentKey(fragmentName: string): string {
  const id = `${fragmentName}${uuid().replace(/-/g, "")}`;
  return id;
}

export const isFieldPresent = (field?: Field | null): boolean => {
  if (field?.value === 0) return true;
  if (Array.isArray(field?.value)) return !!field?.value.length;
  return !!(field?.value || field?.notApplicable || field?.documents?.length);
};

export const isSectionEmpty = (
  content: ContentItem[],
  readFieldFromCache: (key: string) => { field: Field; metadata: Metadata }
): boolean =>
  !content.some(contentItem => {
    if (contentItem.type === "static") {
      return false;
    }
    if (contentItem.type === "field") {
      const { field, metadata } = readFieldFromCache(contentItem.key!);
      if (metadata.displayType === "aum_table") return false;
      return isFieldPresent(field);
    }
    if (contentItem.type === "section") {
      return !isSectionEmpty(contentItem.content!, readFieldFromCache);
    }
    return false;
  });

export const getSectionFieldKeys = (content: ContentItem[]): String[] => {
  return content
    .map(c =>
      c.type === "field"
        ? c.key
        : c.type === "section"
        ? getSectionFieldKeys(c.content)
        : false
    )
    .flat()
    .filter(String) as String[];
};

/**
 * True when the server's copy of a field is newer than the form's.
 *
 * Both timestamps are treated as optional on purpose. react-hook-form
 * materializes a field as `{ value }` when only a subpath is registered
 * (FieldTableContext registers `${templateKey}.value`), so a field that is
 * absent from the form's frozen `defaultValues` is truthy but carries no
 * `modifyDate` — dereferencing it threw during render, and the ErrorBoundary
 * swallowed it into "Something went wrong" (MERCURY-J2). TemplateForm's
 * `setValue(`${ownerType}.${fieldKey}`, change)` also writes the raw server
 * row, whose `modifyDate` is an ISO string rather than a Date, so normalize
 * both sides instead of calling `.getTime()` directly.
 *
 * A missing timestamp on either side means there is nothing to compare
 * against, so there is no server update to surface.
 */
export function hasNewerServerValue(
  currentFormField: { modifyDate?: Date | string | null } | undefined,
  serverField: { modifyDate?: Date | string | null } | undefined
): boolean {
  const formDate = currentFormField?.modifyDate;
  const serverDate = serverField?.modifyDate;
  if (!formDate || !serverDate) return false;
  // An unparseable date yields NaN, and every NaN comparison is false, so a
  // bad timestamp can't produce a spurious update.
  return new Date(formDate).getTime() < new Date(serverDate).getTime();
}
